import { Info } from 'lucide-react';
import type { components } from '@trackercop/contracts';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Popover } from '@/components/ui/popover';
import { formatDateTime } from '@/lib/format';
import {
  COMPONENT_DESCRIPTIONS,
  type ComponentDescription,
} from './health-tile-descriptions';

type ComponentHealth = components['schemas']['ComponentHealth'];
type HealthStatus = components['schemas']['HealthStatus'];
type ComponentName = components['schemas']['ComponentName'];

const STATUS_VARIANT: Record<HealthStatus, NonNullable<BadgeProps['variant']>> = {
  ok: 'success',
  warning: 'warning',
  critical: 'destructive',
  unknown: 'muted',
};

const STATUS_LABEL: Record<HealthStatus, string> = {
  ok: 'OK',
  warning: 'Warning',
  critical: 'Critical',
  unknown: 'Unknown',
};

export function statusVariant(status: HealthStatus): NonNullable<BadgeProps['variant']> {
  return STATUS_VARIANT[status];
}

interface HealthTileProps {
  component: ComponentHealth;
}

export function HealthTile({ component }: HealthTileProps) {
  const description: ComponentDescription | undefined =
    COMPONENT_DESCRIPTIONS[component.name as ComponentName];
  return (
    <div
      className="flex flex-col gap-2 rounded-lg border bg-background p-3 shadow-sm"
      data-testid={`health-tile-${component.name}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium">{component.name}</span>
          {description && (
            <Popover
              align="end"
              className="w-96"
              trigger={
                <Info
                  className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground"
                  aria-label={`About ${component.name}`}
                  data-testid={`health-tile-info-${component.name}`}
                />
              }
            >
              <DescriptionPanel name={component.name} description={description} />
            </Popover>
          )}
        </div>
        <Badge variant={STATUS_VARIANT[component.status]}>
          {STATUS_LABEL[component.status]}
        </Badge>
      </div>
      {component.headlineMetric && (
        <span className="font-mono text-xs text-muted-foreground">
          {component.headlineMetric}
        </span>
      )}
      {component.message && (
        <span className="text-xs text-muted-foreground">{component.message}</span>
      )}
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>
          {typeof component.latencyMs === 'number' ? `${component.latencyMs}ms` : '—'}
        </span>
        <span>{formatDateTime(component.lastCheckedAt)}</span>
      </div>
    </div>
  );
}

function DescriptionPanel({
  name,
  description,
}: {
  name: string;
  description: ComponentDescription;
}) {
  return (
    <div className="flex flex-col gap-3 text-xs">
      <div className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
        {name}
      </div>
      <Section label="What it is" body={description.what} />
      <Section label="What depends on it" body={description.dependsOn} />
      <div className="flex flex-col gap-1">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Status thresholds
        </div>
        <ThresholdRow label="Healthy" body={description.healthy} variant="success" />
        <ThresholdRow label="Warning" body={description.warning} variant="warning" />
        <ThresholdRow label="Critical" body={description.critical} variant="destructive" />
      </div>
      <Section label="If it's broken" body={description.remediation} />
    </div>
  );
}

function Section({ label, body }: { label: string; body: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <p className="leading-snug text-foreground">{body}</p>
    </div>
  );
}

function ThresholdRow({
  label,
  body,
  variant,
}: {
  label: string;
  body: string;
  variant: NonNullable<BadgeProps['variant']>;
}) {
  return (
    <div className="flex items-start gap-2">
      <Badge variant={variant} className="mt-0.5 shrink-0">
        {label}
      </Badge>
      <span className="leading-snug text-foreground">{body}</span>
    </div>
  );
}
